import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import { LucideDynamicIcon } from '@lucide/angular';
import { I18nService } from '../../../core/services/i18n.service';
import { CATEGORIES, ESPACES_AVEC_DONNEES } from './categories';
import { Segment, motsDeRequete, normaliser, scorer, segmenter } from './correspondance';
import { Recent, lireRecents, retenirRecent } from './recents';
import { CategorieApi, RechercheApi, TexteBilingue } from './recherche.api';

/**
 * Une rubrique telle que le menu latéral la décrit. Type STRUCTUREL, volontairement : le composant lit
 * les groupes du menu tels quels (`MainLayout.navGroups()`), il ne tient pas de seconde liste — deux
 * listes finiraient par diverger, et la recherche proposerait une rubrique que le menu n'a plus.
 */
export interface RubriqueSource {
  readonly id: string;
  readonly ar: string;
  readonly fr: string;
  readonly route?: string;
  readonly query?: Readonly<Record<string, string>>;
  readonly icon?: string;
}

export interface GroupeRubriquesSource {
  readonly id: string;
  readonly ar?: string;
  readonly fr?: string;
  readonly items: readonly RubriqueSource[];
  /**
   * Reste une section à part pendant la saisie, au lieu de rejoindre « القائمة ». Pour ce qui n'est pas
   * une rubrique du menu — changer d'espace n'est pas ouvrir un écran de l'espace courant.
   */
  readonly separe?: boolean;
}

/** Une ligne de résultat, prête à rendre. */
interface Resultat {
  /** Identifiant DOM, cible d'`aria-activedescendant`. */
  readonly domId: string;
  readonly libelle: string;
  readonly segments: readonly Segment[];
  /** Le libellé dans l'autre langue — affiché quand c'est lui qui a correspondu. */
  readonly libelleAlterne: string | null;
  /** Ligne secondaire d'un objet métier : sa référence et ce qui le situe (« MIS-2026-014 · STEG »). */
  readonly detail: string | null;
  /** La même, découpée pour surligner ce qui a correspondu (un sigle, un code). */
  readonly detailSegments: readonly Segment[] | null;
  /** Identifiant retenu dans les récents — celui de la ligne d'origine, pas celui de sa copie « récente ». */
  readonly cleRecent: string;
  readonly icone?: string;
  readonly route: string;
  readonly query?: Readonly<Record<string, string>>;
  readonly score: number;
}

interface Section {
  readonly id: string;
  readonly titre: string;
  readonly resultats: readonly Resultat[];
  /** Index, dans la liste APLATIE, du premier résultat de la section. */
  readonly debut: number;
}

/**
 * Recherche globale (⌘K / Ctrl K / « / ») : les rubriques de l'espace courant, et ses objets métier
 * (EEP, missions, notes d'alerte, contrôleurs) servis par `GET /api/recherche`.
 *
 * <p><b>Cloisonnement.</b> Les rubriques proposées sont celles du menu de l'espace où l'on se trouve,
 * et seulement celles-là : un Président qui travaille dans l'espace du Conseil ne se voit pas proposer
 * les écrans de la Présidence. Les objets métier sont cloisonnés par le SERVEUR (rôle de l'espace, puis
 * portée de chaque module) ; ici, on n'affiche que ce qu'il sert, et chaque ligne mène à l'écran de
 * détail DE L'ESPACE COURANT ({@link CATEGORIES}).
 *
 * <p><b>Clavier</b> (modèle ARIA combobox + listbox) : ↑/↓ parcourent tous les résultats, Tab /
 * Maj+Tab sautent d'une section à l'autre, Entrée ouvre, ⌘/Ctrl+Entrée ouvre dans un nouvel onglet,
 * Échap efface la saisie puis ferme. Le focus ne quitte jamais le champ : c'est
 * `aria-activedescendant` qui désigne la ligne active.
 *
 * <p>Fenêtre native `<dialog>` en `showModal()` : couche supérieure, fond rendu inerte par le
 * navigateur, pas de piège à focus à écrire à la main.
 */
@Component({
  selector: 'app-recherche-globale',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideDynamicIcon],
  templateUrl: './recherche-globale.html',
  styleUrl: './recherche-globale.css',
})
export class RechercheGlobale {
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);

  /** Les groupes du menu de l'espace courant. */
  readonly groupes = input.required<readonly GroupeRubriquesSource[]>();
  /** Titre des rubriques sans groupe (le groupe d'accueil du menu n'a pas d'en-tête) : le nom de l'espace. */
  readonly libelleEspace = input<string>('');
  readonly placeholder = input<string>('');
  /** L'espace courant (premier segment de l'URL) — il décide des objets cherchés et de leurs routes. */
  readonly espace = input<string | null>(null);
  /** L'utilisateur connecté (`sub` OIDC) : les récents sont les SIENS, dans l'espace courant. */
  readonly utilisateur = input<string | null>(null);

  /** Ouverts récemment, relus à chaque ouverture (l'espace a pu changer entre-temps). */
  private readonly recents = signal<readonly Recent[]>([]);

  /** « ? » seul dans le champ : la fenêtre montre l'aide des raccourcis au lieu des résultats. */
  readonly aide = computed(() => this.requete().trim() === '?');

  /** Tous les raccourcis de l'application — y compris ⌘B, porté par le menu latéral. */
  readonly raccourcisAide = computed(() => {
    const m = this.estMac ? '⌘' : 'Ctrl';
    const ar = this.lang() === 'ar';
    return [
      { touches: `${m} K`, effet: ar ? 'فتح البحث، من أي مكان' : 'Ouvrir la recherche, où que l’on soit' },
      { touches: '/', effet: ar ? 'فتح البحث (خارج حقول الكتابة)' : 'Ouvrir la recherche (hors champ de saisie)' },
      { touches: '?', effet: ar ? 'عرض هذه الاختصارات' : 'Afficher ces raccourcis' },
      { touches: '↑ ↓', effet: ar ? 'التنقّل بين النتائج' : 'Parcourir les résultats' },
      { touches: 'Tab', effet: ar ? 'القسم الموالي (Shift+Tab : السابق)' : 'Section suivante (Maj+Tab : précédente)' },
      { touches: '↵', effet: ar ? 'فتح النتيجة' : 'Ouvrir le résultat' },
      { touches: `${m} ↵`, effet: ar ? 'فتح النتيجة في نافذة جديدة' : 'Ouvrir dans un nouvel onglet' },
      { touches: 'Esc', effet: ar ? 'مسح الكتابة، ثم الإغلاق' : 'Effacer la saisie, puis fermer' },
      { touches: `${m} B`, effet: ar ? 'إظهار القائمة الجانبية أو إخفاؤها' : 'Afficher / masquer le menu latéral' },
    ];
  });

  private readonly dialogEl = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');
  private readonly champEl = viewChild.required<ElementRef<HTMLInputElement>>('champ');
  private readonly listeEl = viewChild<ElementRef<HTMLElement>>('liste');

  readonly lang = this.i18n.lang;
  readonly requete = signal('');
  /**
   * La ligne active, désignée par son IDENTIFIANT et non par son rang : les résultats du serveur
   * arrivent après les rubriques et s'intercalent — un rang ferait sauter la sélection sous le curseur
   * de l'agent qui vient de la déplacer. `null` = la première ligne.
   */
  private readonly actifId = signal<string | null>(null);
  readonly actif = computed(() => {
    const id = this.actifId();
    const i = id ? this.resultats().findIndex((r) => r.domId === id) : -1;
    return i >= 0 ? i : 0;
  });

  private readonly api = inject(RechercheApi);
  /** Objets métier servis pour la dernière saisie. */
  private readonly donnees = signal<readonly CategorieApi[]>([]);
  readonly chargement = signal(false);

  constructor() {
    // 150 ms après la dernière frappe, et la requête précédente est ANNULÉE (switchMap) : une réponse
    // lente à « ste » ne doit jamais écraser celle, arrivée avant elle, de « steg ».
    toObservable(computed(() => ({ q: this.requete().trim(), espace: this.espace() })))
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) => a.q === b.q && a.espace === b.espace),
        switchMap(({ q, espace }) => {
          if (!espace || !ESPACES_AVEC_DONNEES.has(espace) || q.length < 2) return of([]);
          this.chargement.set(true);
          return this.api.chercher(espace, q).pipe(
            map((r) => r.categories),
            // Serveur indisponible : les rubriques restent, qui ne dépendent pas de lui.
            catchError(() => of([] as readonly CategorieApi[])),
          );
        }),
        tap(() => this.chargement.set(false)),
        takeUntilDestroyed(),
      )
      .subscribe((categories) => this.donnees.set(categories));
  }

  /** Élément qui avait le focus à l'ouverture : il le récupère à la fermeture. */
  private focusPrecedent: HTMLElement | null = null;

  readonly estMac = typeof navigator !== 'undefined' && /macintosh|mac os x/i.test(navigator.userAgent);
  readonly raccourci = this.estMac ? '⌘K' : 'Ctrl K';

  readonly textes = computed(() =>
    this.lang() === 'ar'
      ? {
          ouvrir: 'البحث',
          fermer: 'إغلاق',
          aucun: 'لا نتائج',
          naviguer: 'للتنقّل',
          section: 'القسم الموالي',
          valider: 'للفتح',
          onglet: 'في نافذة جديدة',
          quitter: 'للإغلاق',
          nombre: (n: number) => `${n} نتيجة`,
          // [Provisoire, à relire] « القائمة » est le mot déjà employé par le menu (« تصغير القائمة »).
          rubriques: 'القائمة',
          chargement: 'جارٍ البحث…',
          // [Provisoire, à relire] libellés d'interface, non métier.
          recents: 'فُتحت مؤخرًا',
          aideTitre: 'اختصارات لوحة المفاتيح',
          aideIndice: 'اكتب ? لعرض الاختصارات',
        }
      : {
          ouvrir: 'Rechercher',
          fermer: 'Fermer',
          aucun: 'Aucun résultat',
          naviguer: 'naviguer',
          section: 'section suivante',
          valider: 'ouvrir',
          onglet: 'nouvel onglet',
          quitter: 'fermer',
          nombre: (n: number) => `${n} résultat${n > 1 ? 's' : ''}`,
          rubriques: 'Menu',
          chargement: 'Recherche…',
          recents: 'Ouverts récemment',
          aideTitre: 'Raccourcis clavier',
          aideIndice: 'tapez ? pour les raccourcis',
        },
  );

  /** Les sections à afficher : tout le menu sans saisie, les rubriques qui correspondent sinon. */
  readonly sections = computed<readonly Section[]>(() => {
    const ar = this.lang() === 'ar';
    if (this.aide()) return [];
    const mots = motsDeRequete(this.requete());
    const sections: { id: string; titre: string; resultats: Resultat[]; separe?: boolean }[] = [];

    for (const groupe of this.groupes()) {
      const titre = (ar ? groupe.ar : groupe.fr) ?? (ar ? groupe.fr : groupe.ar) ?? this.libelleEspace();
      const titreNormalise = normaliser(`${groupe.ar ?? ''} ${groupe.fr ?? ''}`);
      const resultats: Resultat[] = [];

      for (const item of groupe.items) {
        // Une entrée sans route est un emplacement réservé : elle ne mène nulle part, elle n'a rien à
        // faire dans une liste dont chaque ligne s'ouvre à l'Entrée.
        if (!item.route) continue;
        const libelle = ar ? item.ar : item.fr;
        const alterne = ar ? item.fr : item.ar;
        const n = normaliser(libelle);
        let segments: Segment[] = [{ texte: libelle, surligne: false }];
        let libelleAlterne: string | null = null;
        let score = 1;

        if (mots.length) {
          const principal = scorer(n, mots);
          // Les deux langues sont cherchées : un agent francophone qui tape « alerte » dans l'interface
          // arabe doit trouver l'entrée, et voir pourquoi (le libellé français s'affiche en second).
          const autre = scorer(normaliser(alterne), mots);
          // Le titre du groupe compte, faiblement : « permanent » remonte tout le contrôle permanent.
          const parGroupe = scorer(titreNormalise, mots).score * 0.2;
          if (principal.score >= autre.score && principal.score > 0) {
            score = principal.score;
            segments = segmenter(libelle, n, principal.plages);
          } else if (autre.score > 0) {
            score = autre.score * 0.9;
            libelleAlterne = alterne;
          } else if (parGroupe > 0) {
            score = parGroupe;
          } else {
            continue;
          }
        }
        resultats.push({
          domId: `rg-${groupe.id}-${item.id}`,
          libelle,
          segments,
          libelleAlterne,
          detail: null,
          detailSegments: null,
          cleRecent: `rg-${groupe.id}-${item.id}`,
          icone: item.icon,
          route: item.route,
          query: item.query,
          score,
        });
      }
      if (!resultats.length) continue;
      // Deux groupes du menu SANS en-tête (l'accueil, et « طلبات المساندة » en fin de menu) prennent
      // tous deux le nom de l'espace : on les réunit, sinon la même section s'afficherait deux fois.
      const existante = sections.find((s) => s.titre === titre);
      if (existante) existante.resultats.push(...resultats);
      else sections.push({ id: groupe.id, titre, resultats, separe: groupe.separe });
    }
    if (!mots.length) return numeroter([...this.sectionRecents(), ...sections]);

    // Avec une saisie, les rubriques se rangent en UNE section : leur groupe de menu importe peu quand
    // on cherche, et six suffisent — au-delà, la saisie est trop vague pour que la liste aide.
    const rubriques = sections
      .filter((s) => !s.separe)
      .flatMap((s) => s.resultats)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
    const toutes = rubriques.length ? [{ id: 'rubriques', titre: this.textes().rubriques, resultats: rubriques }] : [];
    toutes.push(...sections.filter((s) => s.separe));

    if (this.requete().trim().length >= 2) {
      toutes.push(...this.sectionsDonnees(mots, ar));
    }
    // La section qui contient la meilleure réponse passe en tête : l'Entrée doit ouvrir ce qu'on
    // cherchait, pas la première rubrique du menu qui contient la lettre tapée.
    toutes.sort((a, b) => b.resultats[0].score - a.resultats[0].score);
    return numeroter(toutes);
  });

  /** Sans saisie : ce qu'on a ouvert récemment dans CET espace, en tête. */
  private sectionRecents(): { id: string; titre: string; resultats: Resultat[] }[] {
    const resultats = this.recents().map((r): Resultat => ({
      // Préfixe distinct : la même rubrique figure aussi plus bas, dans son groupe — deux lignes, deux ids.
      domId: `rc-${r.domId}`,
      libelle: r.libelle,
      segments: [{ texte: r.libelle, surligne: false }],
      libelleAlterne: null,
      detail: r.detail,
      detailSegments: r.detail ? [{ texte: r.detail, surligne: false }] : null,
      cleRecent: r.domId,
      icone: r.icone,
      route: r.route,
      query: r.query,
      score: 1,
    }));
    return resultats.length ? [{ id: 'recents', titre: this.textes().recents, resultats }] : [];
  }

  /** Les objets métier servis, en sections — seulement ceux qu'une route de l'espace courant ouvre. */
  private sectionsDonnees(mots: readonly string[], ar: boolean): { id: string; titre: string; resultats: Resultat[] }[] {
    const espace = this.espace();
    if (!espace) return [];
    const texte = (t: TexteBilingue | null | undefined) => (ar ? t?.ar || t?.fr : t?.fr || t?.ar) ?? '';
    return this.donnees().flatMap((categorie) => {
      const def = CATEGORIES[categorie.code];
      const modele = def?.routes[espace];
      if (!def || !modele) return [];
      const resultats = categorie.resultats.flatMap((r): Resultat[] => {
        const route = construireRoute(modele, r.id, r.parametres);
        if (!route) return [];
        const modeleRequete = def.requetes?.[espace];
        const requete = modeleRequete ? construireRequete(modeleRequete, r.id, r.parametres) : undefined;
        if (requete === null) return [];
        const query = requete ?? undefined;
        const libelle = texte(r.titre);
        const n = normaliser(libelle);
        const titre = scorer(n, mots);
        const reference = r.reference ? scorer(normaliser(r.reference), mots).score * 1.1 : 0;
        const contexte = texte(r.contexte);
        const parContexte = contexte ? scorer(normaliser(contexte), mots).score * 0.5 : 0;
        const detail = [r.reference, contexte].filter(Boolean).join(' · ') || null;
        const nDetail = detail ? normaliser(detail) : null;
        const domId = `rg-${categorie.code}-${r.id}`;
        return [{
          domId,
          libelle,
          segments: segmenter(libelle, n, titre.plages),
          libelleAlterne: null,
          detail,
          detailSegments: detail && nDetail ? segmenter(detail, nDetail, scorer(nDetail, mots).plages) : null,
          cleRecent: domId,
          icone: def.icone,
          route,
          query,
          // Le serveur l'a trouvée : elle correspond, même quand le mot n'est que dans un champ non affiché.
          score: Math.max(titre.score, reference, parContexte, 1),
        }];
      });
      resultats.sort((a, b) => b.score - a.score);
      return resultats.length ? [{ id: categorie.code, titre: ar ? def.ar : def.fr, resultats }] : [];
    });
  }

  readonly resultats = computed(() => this.sections().flatMap((s) => s.resultats));

  readonly idActif = computed(() => this.resultats()[this.actif()]?.domId ?? null);

  // ——————————————————————————— Ouverture / fermeture ———————————————————————————

  ouvrir(texte = ''): void {
    const dialog = this.dialogEl().nativeElement;
    if (dialog.open) {
      if (texte) this.surSaisie(texte);
      this.champEl().nativeElement.select();
      return;
    }
    this.focusPrecedent = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.recents.set(lireRecents(this.utilisateur(), this.espace()));
    this.requete.set(texte);
    this.actifId.set(null);
    dialog.showModal();
    this.champEl().nativeElement.focus();
  }

  fermer(): void {
    const dialog = this.dialogEl().nativeElement;
    if (dialog.open) dialog.close();
  }

  /** Événement `close` du dialog : qu'il vienne d'Échap, d'un clic ou d'une navigation. */
  surFermeture(): void {
    this.focusPrecedent?.focus();
    this.focusPrecedent = null;
  }

  /**
   * Échap, reçu par l'événement `cancel` du dialog (et non par un keydown, que le navigateur traite
   * avant nous) : le premier efface la saisie — on voulait la corriger —, le second ferme.
   */
  surAnnulation(event: Event): void {
    if (!this.requete()) return;
    event.preventDefault();
    this.surSaisie('');
  }

  /** Clic sur le voile : la cible est le `<dialog>` lui-même, pas son panneau. */
  surClicDialog(event: MouseEvent): void {
    if (event.target === this.dialogEl().nativeElement) this.fermer();
  }

  // ——————————————————————————— Clavier ———————————————————————————

  /**
   * Raccourcis d'ouverture, où que l'on soit.
   *
   * <p>⌘/Ctrl+K vaut même dans un champ de saisie (convention Slack, Linear, GitHub) : c'est une
   * combinaison, elle ne peut pas être une frappe. « / » est une frappe : il n'ouvre que hors champ.
   */
  @HostListener('window:keydown', ['$event'])
  surToucheGlobale(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.ouvrir();
      return;
    }
    if (event.metaKey || event.ctrlKey || estEditable(document.activeElement)) return;
    if (event.key === '/') {
      event.preventDefault();
      this.ouvrir();
    } else if (event.key === '?') {
      // « ? » hors champ : l'aide des raccourcis, où que l'on soit (convention GitHub, Gmail).
      event.preventDefault();
      this.ouvrir('?');
    }
  }

  surSaisie(valeur: string): void {
    this.requete.set(valeur);
    this.actifId.set(null);
    this.listeEl()?.nativeElement.scrollTo({ top: 0 });
  }

  surToucheChamp(event: KeyboardEvent): void {
    const total = this.resultats().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (total) this.activer((this.actif() + 1) % total);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (total) this.activer((this.actif() - 1 + total) % total);
        break;
      case 'Tab':
        // Le focus RESTE dans le champ : Tab change de section, il ne sort pas de la fenêtre.
        event.preventDefault();
        this.sauterSection(event.shiftKey ? -1 : 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const r = this.resultats()[this.actif()];
        if (r) this.ouvrirResultat(r, event.metaKey || event.ctrlKey);
        break;
      }
    }
  }

  /** Aller au premier résultat de la section suivante (ou précédente), en boucle. */
  private sauterSection(sens: 1 | -1): void {
    const sections = this.sections();
    if (!sections.length) return;
    const courante = sections.findIndex(
      (s) => this.actif() >= s.debut && this.actif() < s.debut + s.resultats.length,
    );
    const cible = sections[(courante + sens + sections.length) % sections.length];
    this.activer(cible.debut);
  }

  activer(index: number): void {
    const id = this.resultats()[index]?.domId;
    if (!id) return;
    this.actifId.set(id);
    // Après le rendu : la ligne active doit rester visible quand on la fait défiler au clavier.
    queueMicrotask(() =>
      this.dialogEl().nativeElement.querySelector(`#${CSS.escape(id)}`)?.scrollIntoView({ block: 'nearest' }),
    );
  }

  ouvrirResultat(r: Resultat, nouvelOnglet = false): void {
    this.recents.set(
      retenirRecent(this.utilisateur(), this.espace(), {
        domId: r.cleRecent,
        libelle: r.libelle,
        detail: r.detail,
        icone: r.icone,
        route: r.route,
        query: r.query,
      }),
    );
    const arbre = this.router.createUrlTree([r.route], { queryParams: r.query });
    if (nouvelOnglet) {
      window.open(this.router.serializeUrl(arbre), '_blank', 'noopener');
      return;
    }
    // On ne rend PAS le focus à l'élément d'origine quand on part ailleurs : il appartient à l'écran
    // qu'on quitte. C'est la page d'arrivée (lien d'évitement, titre) qui doit le recevoir.
    this.focusPrecedent = null;
    this.fermer();
    void this.router.navigateByUrl(arbre);
  }

  surClicResultat(event: MouseEvent, r: Resultat): void {
    event.preventDefault();
    this.ouvrirResultat(r, event.metaKey || event.ctrlKey);
  }

  /** `href` réel sur chaque ligne : clic du milieu et « ouvrir dans un nouvel onglet » fonctionnent. */
  href(r: Resultat): string {
    return this.router.serializeUrl(this.router.createUrlTree([r.route], { queryParams: r.query }));
  }
}

function estEditable(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

/** Numérote les sections : chacune connaît le rang, dans la liste aplatie, de son premier résultat. */
function numeroter(sections: readonly { id: string; titre: string; resultats: Resultat[] }[]): Section[] {
  let debut = 0;
  return sections.map((s) => {
    const section = { ...s, debut };
    debut += s.resultats.length;
    return section;
  });
}

/** Les paramètres de requête d'un résultat, sur le modèle de la catégorie ; `null` s'il en manque un. */
function construireRequete(
  modele: Readonly<Record<string, string>>,
  id: string,
  parametres: Readonly<Record<string, string>> | null | undefined,
): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const [cle, gabarit] of Object.entries(modele)) {
    const nom = gabarit.startsWith(':') ? gabarit.substring(1) : null;
    const valeur = nom === null ? gabarit : nom === 'id' ? id : parametres?.[nom];
    if (!valeur) return null;
    out[cle] = valeur;
  }
  return out;
}

/**
 * Remplit un modèle de route (`/controleur/rapport-annuel/:year/:id`) : `:id` par l'identifiant, les
 * autres par les paramètres servis. Un paramètre manquant rend `null` — la ligne n'est pas affichée,
 * plutôt que de mener à une URL tronquée.
 */
function construireRoute(
  modele: string,
  id: string,
  parametres: Readonly<Record<string, string>> | null | undefined,
): string | null {
  let manquant = false;
  const route = modele.replace(/:([a-zA-Z]+)/g, (_, nom: string) => {
    const valeur = nom === 'id' ? id : parametres?.[nom];
    if (!valeur) manquant = true;
    return encodeURIComponent(valeur ?? '');
  });
  return manquant ? null : route;
}
